// src/services/pushNotificationService.js
// 前端：註冊 Service Worker、要權限、訂閱推播、把訂閱送到後端。
//
// 透過專案共用的 apiService（baseURL 已含 /api，故路徑用相對的 'push/...'）。
// 後端 /api/push/* 為 toy-app / booking 共用（同一 backend），端點完全一致。
import apiService from './apiService';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// 是否為「已加到主畫面」的 PWA（iOS Web Push 必要條件）
export function isStandalonePWA() {
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register('/service-worker.js');
}

// 目前是否已訂閱（給 UI 顯示開關狀態用）
export async function isPushEnabled() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

// 使用者點「開啟新訂位通知」時呼叫
export async function enablePush() {
  if (!pushSupported()) throw new Error('此裝置/瀏覽器不支援 Web Push');

  const reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('使用者未允許通知');

  const { data } = await apiService.apiRequest('get', 'push/vapid-public-key');
  if (!data || !data.publicKey) throw new Error('伺服器尚未設定 VAPID 金鑰');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey)
    });
  }
  // sub.toJSON() => { endpoint, keys: { p256dh, auth } } — 正好對上後端 controller
  await apiService.apiRequest('post', 'push/subscribe', sub.toJSON());
  return true;
}

// 使用者關閉通知 / 登出時呼叫
export async function disablePush() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    // DELETE 需透過 config.data 帶 body（apiService 的 delete 不會帶 data 參數）
    try {
      await apiService.apiRequest('delete', 'push/subscribe', null, false, {
        data: { endpoint: sub.endpoint }
      });
    } catch (e) {
      // 後端取消失敗不阻擋本地退訂
      console.warn('取消推播訂閱（後端）失敗:', e?.message);
    }
    await sub.unsubscribe();
  }
}

// 送一則測試推播給自己（QA 用）
export async function sendTestPush() {
  const { data } = await apiService.apiRequest('post', 'push/test');
  return data;
}
